import { useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg, download, itDate } from "@/lib/api";
import { toast } from "sonner";
import { FilePdf, HardHat, Wrench, Certificate, GraduationCap, Receipt, FileText } from "@phosphor-icons/react";

const CATEGORIE = [
  { key: "buste", label: "Buste Paga", icon: Receipt, types: ["BUSTA_PAGA"] },
  { key: "contratti", label: "Contratti", icon: FileText, types: ["CONTRATTO"] },
  { key: "haccp", label: "Attestati HACCP", icon: Certificate, types: ["HACCP"] },
  { key: "formazione", label: "Attestati di Formazione", icon: GraduationCap, types: ["FORMAZIONE"] },
  { key: "dpi", label: "Consegne DPI", icon: HardHat, types: [] },
  { key: "attrezzature", label: "Consegne Attrezzature da Lavoro", icon: Wrench, types: [] },
  { key: "altri", label: "Altri documenti", icon: FilePdf, types: ["ALTRO"] },
];

const soloAttrezzature = (d) => (d.items || []).length > 0 &&
  (d.items || []).every((i) => (i.kind || "").toUpperCase() === "ATTREZZATURA");

export default function MyDocuments() {
  const [docs, setDocs] = useState([]);
  const [consegne, setConsegne] = useState([]);
  const [attiva, setAttiva] = useState("buste");

  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/safety/my-deliveries").then((r) => setConsegne(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const perCategoria = useMemo(() => {
    const noti = CATEGORIE.flatMap((c) => c.types);
    return {
      buste: docs.filter((d) => d.doc_type === "BUSTA_PAGA"),
      contratti: docs.filter((d) => d.doc_type === "CONTRATTO"),
      haccp: docs.filter((d) => d.doc_type === "HACCP"),
      formazione: docs.filter((d) => d.doc_type === "FORMAZIONE"),
      dpi: consegne.filter((d) => !soloAttrezzature(d)),
      attrezzature: consegne.filter(soloAttrezzature),
      altri: docs.filter((d) => !noti.includes(d.doc_type) || d.doc_type === "ALTRO"),
    };
  }, [docs, consegne]);

  const visibili = CATEGORIE.filter((c) => c.key !== "altri" || perCategoria.altri.length > 0);
  const corrente = visibili.find((c) => c.key === attiva) || visibili[0];

  const apri = (d) => download(`/documents/${d.id}/file`,
    `${(d.title || d.original_filename || "documento").replace(/[^\w .-]+/g, "_")}.pdf`)
    .catch((e) => toast.error(errMsg(e)));

  const righe = perCategoria[corrente?.key] || [];
  const isConsegna = corrente?.key === "dpi" || corrente?.key === "attrezzature";

  return (
    <div className="space-y-4 max-w-3xl" data-testid="my-documents-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">I miei documenti</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Scegli la categoria: ogni sezione mostra solo i documenti che le appartengono.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="my-docs-tabs">
        {visibili.map((c) => {
          const Icon = c.icon;
          const n = (perCategoria[c.key] || []).length;
          return (
            <button key={c.key} type="button" onClick={() => setAttiva(c.key)}
              data-testid={`my-docs-tab-${c.key}`}
              className={`flex items-center gap-1.5 h-9 px-3 rounded-sm border text-sm font-semibold transition-colors duration-150
                ${corrente?.key === c.key ? "border-primary bg-primary/10 text-primary"
                : "border-border hover:bg-secondary"}`}>
              <Icon size={15} /> {c.label}
              <span className="tabular-nums text-[11px] text-muted-foreground">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="rounded-sm border border-border bg-card p-4 space-y-1.5"
        data-testid={`my-docs-list-${corrente?.key}`}>
        {isConsegna && righe.map((d) => (
          <button key={d.id} type="button" data-testid={`my-doc-delivery-${d.progressive}`}
            onClick={() => download(`/safety/deliveries/${d.id}/pdf`,
              `consegna_${corrente.key}_${d.progressive}.pdf`).catch((e) => toast.error(errMsg(e)))}
            className="w-full flex items-center justify-between gap-2 rounded-sm border border-border px-3 py-2 text-sm hover:bg-secondary transition-colors duration-150">
            <span className="flex items-center gap-2 min-w-0">
              <FilePdf size={16} className="shrink-0 text-primary" />
              <span className="truncate">
                Consegna n. {d.progressive} · {(d.items || []).map((i) => i.description).join(", ")}
              </span>
            </span>
            <span className="tabular-nums text-xs text-muted-foreground shrink-0">{itDate(d.date)}</span>
          </button>
        ))}

        {!isConsegna && righe.map((d) => (
          <button key={d.id} type="button" data-testid={`my-doc-${d.id}`} onClick={() => apri(d)}
            className="w-full flex items-center justify-between gap-2 rounded-sm border border-border px-3 py-2 text-sm hover:bg-secondary transition-colors duration-150">
            <span className="flex items-center gap-2 min-w-0">
              <FilePdf size={16} className="shrink-0 text-primary" />
              <span className="truncate">{d.title || d.original_filename}</span>
            </span>
            <span className="tabular-nums text-xs text-muted-foreground shrink-0">
              {d.period || (d.created_at ? new Date(d.created_at).toLocaleDateString("it-IT") : "")}
            </span>
          </button>
        ))}

        {righe.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="my-docs-empty">
            Nessun documento in questa categoria.
          </p>
        )}
      </div>
    </div>
  );
}
